<template>
    <div>
        <b-row>
            <b-col class="ml-2 mr-2">
                Below is a summary of your declaration. By clicking on the approve declaration button the declaration
                will be sent to your customer for review. Your customer will need to approve your declaration before
                the task is marked as completed. You will set a validity periodfor this declaration. Once the period
                has expired Hamlet will automatically send you a request for update.
            </b-col>
        </b-row>
        <b-row>
            <b-col class="m-2">
                <b-row class="mb-1">
                    <b-col md="6" lg="6">
                        <ProductInformation
                            :product="product"
                        />
                    </b-col>
                    <b-col md="6" lg="6">
                        <div>
                            <Conversation :request-id="request.id" />
                        </div>
                    </b-col>
                </b-row>
                <b-card border-variant="light" :title="$t('Component(ProductCardTabs):composition-label')">
                    <div v-if="product.isComplex">
                        <ComponentList
                            :components="components"
                        />
                    </div>
                    <div v-else>
                        <InstructionText
                            :variant="substanceDetailsMessage.variant"
                        >
                            {{ substanceDetailsMessage.message }}
                        </InstructionText>
                        <Substance
                            v-for="substance in includedSubstances"
                            :read-only="true"
                            :compact-view="false"
                            :substance="substance"
                            :substance-lists="substanceLists"
                        />
                    </div>
                </b-card>
                <b-card border-variant="light" :title="$t('Component(VerificationDocuments):label')">
                    <Verification
                        :read-only="true"
                        :product="product"
                        :id="product.id"
                        :is-safe-use-applicable="product.isSafeUseApplicable"
                        :doc="declarationOfConformityDocuments"
                        :evidence="evidenceReports"
                        :compliant-statement="compliantStatementDocuments"
                        :safe-use="safeUseDocument"
                        :disassembling="dissemblingInstructionsDocument"
                        :compliant-statement-border-variant="compliantStatementStyle.border"
                        :dissembling-border-variant="dissemblingStyle.border"
                        :evidence-report-border-variant="evidenceReportStyle.border"
                        :doc-border-variant="docStyle.border"
                        :safe-user-border-variant="safeUseStyle.border"
                        @documentSelected="onDocumentSelected"
                    />
                </b-card>
                <OtherReplyInformation
                    :request="request"
                    declaration-validity-class=""
                    @input="onOtherInformationChange"
                />
            </b-col>
        </b-row>
    </div>
</template>

<script>
import {
    BCard, BRow, BCol, BFormCheckbox, BFormTextarea,
    BFormDatepicker, BCardGroup, BFormGroup, BFormInput, BAlert,
    BBadge, BCardTitle, BCardHeader, BListGroup, BListGroupItem,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ProductInformation from "@/views/request/received/components/ProductInformation";
import ConcernElement from "@/views/request/received/components/ConcernElement";
import DeclarationApproval from "@/views/request/received/components/DeclarationApproval";
import InstructionSelector from "@/views/request/received/components/send-reply/InstructionSelector";
import {SubstanceState, VerificationDocumentType} from "@/libs/product";
import {Conversation} from "@core/components/messenger";
import ReplyTabProductSection from "@core/mixins/ui/reply-tab-product-section";
import OtherReplyInformation from "@/views/request/received/components/send-reply/OtherReplyInformation";
import DeclarationValidity from "@/views/request/received/components/DeclarationValidity";
import {Verification} from "@/views/product/components/product-card";
import {Substance} from "@/views/product/components/product-card/composition";
import {ProductProvider} from "@core/domain/product";
import ComponentList from "@/views/product/components/product-card/composition/ComponentList.vue";
import InstructionText from "@core/components/common/instructions/InstructionText.vue";
import {getAllSubstanceLists} from "@core/domain/substance";
import ActiveSubstanceListsMixin from "@/views/product/components/product-card/ActiveSubstanceListsMixin";

export default {
    mixins: [ReplyTabProductSection, ActiveSubstanceListsMixin],
    components: {
        InstructionText, ComponentList, Substance, Verification,
        BCard, BRow, BCol, Conversation, OtherReplyInformation, DeclarationValidity, BFormCheckbox, BListGroup,
        BListGroupItem, BFormTextarea, BFormDatepicker, BCardGroup, BCardTitle,
        BFormGroup, BFormInput, BAlert, BBadge, BCardHeader, ConcernElement, DeclarationApproval,
        InstructionSelector, ProductInformation, vSelect,
    },
    methods: {
        onDocumentSelected(selected, document) {
            const index = this.selectedDocuments.findIndex(d => d.id === document.id);
            if (selected) {
                if (index === -1) {
                    this.selectedDocuments.push(document);
                }
            } else {
                if (index !== -1) {
                    this.selectedDocuments.splice(index, 1);
                }
            }

            this.validateRequiredInformationAndEmit();
        },

        onOtherInformationChange(value) {
            this.$emit('otherInformation', value)
        },

        onDeclarationConfirm() {
            this.$emit('replyTabIsValid')
        },

        declarationValid() {
            this.$emit('valid', this.makeDeclaration());
        },

        makeDeclaration() {
            return {
                documents: this.selectedDocuments.map(d => d),
                declarationValidity: this.declarationValidity,
            };
        },

        declarationInvalid() {
            this.$emit('invalid', this.makeDeclaration())
        },

        loadComplexProductComponentsSubstances() {
            if (this.componentsLoaded) {
                return;
            }
            this.componentsLoaded = true;
            ProductProvider(this.product.id)
                .getComponentsSubstances()
                .then(components => {
                    this.components = components;
                });
        },

        validateRequiredInformationAndEmit() {
            if (this.request.verificationDocuments.includes(VerificationDocumentType.EVIDENCE_REPORT)) {
                if (this.selectedDocuments.filter(d => d.type === VerificationDocumentType.EVIDENCE_REPORT).length === 0) {
                    this.declarationInvalid();
                    return;
                }
            }

            if (this.request.verificationDocuments.includes(VerificationDocumentType.DECLARATION_OR_CONFORMITY)) {
                if (this.selectedDocuments.filter(d => d.type === VerificationDocumentType.DECLARATION_OR_CONFORMITY).length === 0) {
                    this.declarationInvalid();
                    return;
                }
            }

            if (this.request.verificationDocuments.includes(VerificationDocumentType.SAFE_USE_INSTRUCTIONS)) {
                const hasSafeUse = this.selectedDocuments.filter(d => d.type === VerificationDocumentType.SAFE_USE_INSTRUCTIONS).length > 0;
                if (!hasSafeUse && this.product.isSafeUseApplicable !== false) {
                    this.declarationInvalid();
                    return;
                }
            }

            if (this.request.verificationDocuments.includes(VerificationDocumentType.DISSEMBLING_INSTRUCTIONS)) {
                const hasDissembling = this.selectedDocuments.filter(d => d.type === VerificationDocumentType.DISSEMBLING_INSTRUCTIONS).length > 0;
                if (!hasDissembling) {
                    this.declarationInvalid();
                    return;
                }
            }

            if (true === this.hasConcernSubstances || null === this.hasConcernSubstances) {
                if (this.includedSubstances.length === 0) {
                    this.declarationInvalid();
                    return;
                }
            }

            this.declarationValid();
        },

        retrieveSubstanceLists() {
            getAllSubstanceLists().then(lists => {
                this.substanceLists = this.activeSubstanceLists(lists, this.request.legislations.map(l => l.id));
            });
        }
    },
    props: {
        isSafeUseApplicable: {
            default: true
        },
        declarationOfConformityDocuments: {
            type: Array,
            required: true
        },
        compliantStatementDocuments: {
            type: Array,
            required: true
        },
        evidenceReports: {
            type: Array,
            required: true
        },
        safeUseDocument: {
            type: Object,
            default() {
                return {

                };
            }
        },
        dissemblingInstructionsDocument: {
            type: Object,
            default() {
                return {

                };
            }
        },
        request: {
            required: true
        },
        product: {
            required: true
        },
        includedSubstances: {
            required: true,
            default() {
                return [];
            }
        },
        hasConcernSubstances: {
            required: true,
        }
    },
    data() {
        return {
            customerInformation: '',
            internalComment: '',

            evidenceReportsSelected: [],
            declarationOfConformitySelected: [],
            isSafeUseSelected: false,
            isDissemblingSelected: false,

            selectedDocuments: [],

            components: [],
            componentsLoaded: false,

            substanceLists: []
        };
    },

    computed: {
        substanceState() {
            return this.request.substanceState;
        },
        substanceDetailsMessage() {
            switch (this.substanceState) {
                case SubstanceState.ALL_DEFINED_DOES_NOT_INCLUDE_SUBSTANCES:
                    return {
                        message: this.$t('Page(ReceivedRequest):Component(ConcernElements):message-all-declared-not-includes'),
                        variant: 'success'
                    };

                case SubstanceState.ALL_DEFINED_INCLUDES_SUBSTANCES:
                    return {
                        message: this.$t('Page(ReceivedRequest):Component(ConcernElements):message-all-declared-includes'),
                        variant: 'success'
                    };

                case SubstanceState.SOME_DEFINED_DOES_NOT_INCLUDE_SUBSTANCES:
                case SubstanceState.SOME_DEFINED_INCLUDES_SUBSTANCES:
                case SubstanceState.ALL_UNDEFINED:
                default:
                    return {
                        message: this.$t('Page(ReceivedRequest):Component(ConcernElements):message-not-all-declared'),
                        variant: 'danger'
                    };
            }
        },
        docStyle() {
            let state = 'required';
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.DECLARATION_OR_CONFORMITY)) {
                state = 'default';
            } else if (this.selectedDocuments.filter(d => d.type === VerificationDocumentType.DECLARATION_OR_CONFORMITY).length > 0) {
                state = 'success';
            }
            return this.$store.state.request.received.replyVariant[state];
        },
        evidenceReportStyle() {
            let state = 'required';
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.EVIDENCE_REPORT)) {
                state = 'default';
            } else if (this.selectedDocuments.filter(d => d.type === VerificationDocumentType.EVIDENCE_REPORT).length > 0) {
                state = 'success';
            }
            return this.$store.state.request.received.replyVariant[state];
        },
        compliantStatementStyle() {
            let state = 'required';
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.COMPLIANT_STATEMENT)) {
                state = 'default';
            } else if (this.selectedDocuments.filter(d => d.type === VerificationDocumentType.COMPLIANT_STATEMENT).length > 0) {
                state = 'success';
            }
            return this.$store.state.request.received.replyVariant[state];
        },
        safeUseStyle() {
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.SAFE_USE_INSTRUCTIONS)) {
                return this.$store.state.request.received.replyVariant.default;
            }
            if (false === this.isSafeUseApplicable) {
                return this.$store.state.request.received.replyVariant.success;
            }
            let state = 'required';
            if (this.safeUseDocument && this.safeUseDocument.id) {
                state = (this.safeUseDocument.description) ? 'success' : 'required';
            }

            return this.$store.state.request.received.replyVariant[state];
        },
        dissemblingStyle() {
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.DISSEMBLING_INSTRUCTIONS)) {
                return this.$store.state.request.received.replyVariant.default;
            }

            let state = 'required';
            if (this.dissemblingInstructionsDocument && this.dissemblingInstructionsDocument.id) {
                state = (this.dissemblingInstructionsDocument.file || this.dissemblingInstructionsDocument.description) ? 'success' : 'required';
            }
            return this.$store.state.request.received.replyVariant[state];
        },
    },

    watch: {
        product() {
            if (this.product.isComplex) {
                this.loadComplexProductComponentsSubstances();
            } else {
                this.retrieveSubstanceLists();
            }
        },
        includedSubstances(substances) {
            this.validateRequiredInformationAndEmit();
        },
        hasConcernSubstances() {
            this.validateRequiredInformationAndEmit();
        }
    }
}
</script>

<style type="text/css">
table tbody th {
    text-align: right;
    padding: 4px;
}
</style>
